import React, { useState, useEffect, useRef } from 'react';
import { Box, Image } from '@chakra-ui/react';
import SuperTele from '../images/supertele.png';

/**
 * Generate a random color for the ball.
 */
function randomColor() {
  const r = () => Math.floor(Math.random() * 256);
  return `rgb(${r()}, ${r()}, ${r()})`;
}

/**
 * Create a new ball simulating a "catapult" launch.
 *
 *  1. The ball starts near the bottom-left of the screen.
 *  2. We pick a random angle (somewhere between ~30° and ~80°).
 *  3. We pick a random initial speed ("power").
 *  4. We apply only gravity, so horizontal velocity (vx) won't slow.
 *  5. We also assign a random rotation speed.
 */
function createNewBall() {
  const diameter = 100; // fixed diameter for now

  // Start near the bottom-left corner
  const x = 50;
  const y = window.innerHeight - diameter - 50;

  // Random angle between 30° and 80°
  const angleDeg = 30 + Math.random() * 50;
  const angleRad = (angleDeg * Math.PI) / 180;

  // Random initial speed
  const speed = 10 + Math.random() * 25; // between 10 and 35
  const vx = speed * Math.cos(angleRad);
  const vy = -speed * Math.sin(angleRad);

  const gravity = 0.6;
  const friction = 1.005;

  // Add rotation properties: starting angle and random rotation speed
  const rotation = 0;
  // Random rotation speed between -5 and +5 degrees per frame
  const rotationSpeed = Math.random() * 10 - 5;

  return {
    id: Date.now() + Math.random(),
    x,
    y,
    vx,
    vy,
    diameter,
    gravity,
    friction,
    color: randomColor(),
    rotation, // initial rotation angle in degrees
    rotationSpeed, // degrees to rotate per frame
  };
}

/**
 * Update the ball's position and rotation each frame using basic physics:
 *   x  -> x + vx
 *   y  -> y + vy
 *   vy -> vy + gravity
 *   rotation -> rotation + rotationSpeed
 */
function updateBall(ball) {
  const { x, y, vx, vy, gravity, friction, rotation, rotationSpeed } = ball;

  const newVx = vx * friction;
  const newVy = vy + gravity;
  const newX = x + newVx;
  const newY = y + newVy;
  const newRotation = rotation + rotationSpeed;

  return {
    ...ball,
    x: newX,
    y: newY,
    vx: newVx,
    vy: newVy,
    rotation: newRotation,
  };
}

/**
 * Determine if the ball is off-screen enough to remove it.
 */
function isOffScreen(ball) {
  const buffer = 200;
  return (
    ball.x > window.innerWidth + buffer ||
    ball.x < -buffer ||
    ball.y > window.innerHeight + buffer ||
    ball.y < -buffer
  );
}

/**
 * RandomBalls (Catapult Edition)
 *
 * Spawns new balls at random angles and speeds, updates their position and rotation,
 * and removes them when they go off-screen.
 */
function RandomBalls() {
  const [balls, setBalls] = useState([]);
  const requestRef = useRef(null);

  // Spawn a new ball every 60 seconds (adjust as needed)
  useEffect(() => {
    const intervalId = setInterval(() => {
      setBalls((prevBalls) => [...prevBalls, createNewBall()]);
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  // Animation loop
  useEffect(() => {
    const animate = () => {
      setBalls((prevBalls) =>
        prevBalls.map(updateBall).filter((b) => !isOffScreen(b))
      );
      requestRef.current = requestAnimationFrame(animate);
    };

    requestRef.current = requestAnimationFrame(animate);

    return () => cancelAnimationFrame(requestRef.current);
  }, []);

  return (
    <Box
      position="fixed"
      zIndex={balls.length ? 10000000 : -1}
      w="100%"
      h="100vh"
      overflow="hidden"
    >
      {balls.map((ball) => (
        <Image
          key={ball.id}
          position="absolute"
          width={`${ball.diameter}px`}
          height={`${ball.diameter}px`}
          left={`${ball.x}px`}
          top={`${ball.y}px`}
          borderRadius="50%"
          boxShadow="2xl"
          src={SuperTele}
          // Apply rotation using the transform property
          transform={`rotate(${ball.rotation}deg)`}
        />
      ))}
    </Box>
  );
}

export default RandomBalls;
